// GA4 + Meta Pixel helpers for the customer booking funnel.
//
// The gtag loader, the Meta Pixel base code and both Consent Mode defaults
// live in public/index.html (the same GA4 property and pixel ID as
// www.alfrescogrand.com). Everything here is a thin, fail-safe wrapper: if
// gtag or fbq is missing (tests, blocked script, non-production host where the
// pixel is never loaded) every call is a no-op.
//
// PRIVACY RULE: nothing that identifies a person is ever sent — no names,
// phones, emails, passenger lists. Booking value, cruise type, deck and party
// counts are the only booking facts that leave the app, to either vendor.
//
// PARTNER RULE: the partner portal (/partner/*) sends no Meta events at all —
// partner bookings are trade, not ad conversions. GA4 achieves this by never
// being called from partner screens; Meta additionally checks the path here
// because PageView is driven by the router (see trackPageView).

const CONSENT_COOKIE = 'ag_consent';
const CONSENT_MAX_AGE = 15552000; // ~180 days, same as the marketing site
const ROOT_DOMAIN = 'alfrescogrand.com';
const CURRENCY = 'INR';

// First-touch traffic source, shared with the marketing site (Layout.astro
// writes the identical cookie): 30 days, on .alfrescogrand.com, consent-gated.
const SOURCE_COOKIE = 'ag_src';
const SOURCE_MAX_AGE = 2592000; // 30 days
const SOURCE_PENDING_KEY = 'ag_src_pending';
const SOURCE_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'fbclid'];

const PARTNER_PATH = /^\/partner(\/|$)/;

function gtag() {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  try {
    window.gtag.apply(null, arguments);
  } catch (_) {
    /* analytics must never break the booking flow */
  }
}

function fbq() {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  try {
    window.fbq.apply(null, arguments);
  } catch (_) {
    /* analytics must never break the booking flow */
  }
}

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return m ? m[1] : null;
}

/** 'granted' | 'denied' | null (no choice recorded yet). */
export function readConsent() {
  const v = readCookie(CONSENT_COOKIE);
  return v === 'granted' || v === 'denied' ? v : null;
}

// The cookie is written on the parent domain so www. and book. share one
// choice. On localhost / preview hosts the domain attribute would be rejected
// by the browser, so it is omitted there (host-only cookie).
function cookieDomainAttr() {
  const h = window.location.hostname;
  return h === ROOT_DOMAIN || h.endsWith(`.${ROOT_DOMAIN}`) ? `; domain=.${ROOT_DOMAIN}` : '';
}

/** Persist the visitor's choice and, if granted, unlock analytics storage,
 *  Google Ads click measurement (ad_storage + ad_user_data), the Meta Pixel
 *  and the pending traffic-source cookie. ad_personalization is never
 *  granted — no remarketing, as the privacy policy states. Decline sends an
 *  explicit denied update even though the default (public/index.html)
 *  already denies everything: the explicit signal reads better for Google's
 *  modelling. */
export function setConsent(value) {
  document.cookie = `${CONSENT_COOKIE}=${value}; max-age=${CONSENT_MAX_AGE}; path=/${cookieDomainAttr()}; SameSite=Lax`;
  if (value === 'granted') {
    gtag('consent', 'update', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      analytics_storage: 'granted',
    });
    // The pixel was initialised revoked (public/index.html); granting flushes
    // its queued PageView and lets it set _fbp/_fbc from here on.
    fbq('consent', 'grant');
    promoteLanding();
  } else {
    gtag('consent', 'update', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
    });
  }
}

/** Fire a GA4 event. Params must already be PII-free. */
export function trackEvent(name, params = {}) {
  gtag('event', name, params);
}

/**
 * Fire a Meta Pixel standard event. Params must already be PII-free.
 * `options.eventID` is passed through for deduplication (Purchase uses the
 * booking reference, the same value GA4 sends as transaction_id, so a future
 * Conversions API event can be matched to it). Never fires on partner routes.
 */
export function trackMeta(name, params = {}, options) {
  if (typeof window !== 'undefined' && PARTNER_PATH.test(window.location.pathname)) return;
  if (options && options.eventID) fbq('track', name, params, { eventID: options.eventID });
  else fbq('track', name, params);
}

// The base code in public/index.html already sent PageView for the document
// that booted the app, so the router-driven PageView must skip that path once
// (and StrictMode's doubled effects, and re-renders with the same path).
let lastPageViewPath = typeof window !== 'undefined' ? window.location.pathname : null;

/** PageView for a client-side route change. Partner routes never send one. */
export function trackPageView(pathname) {
  if (pathname === lastPageViewPath) return;
  lastPageViewPath = pathname;
  if (PARTNER_PATH.test(pathname)) return;
  fbq('track', 'PageView');
}

/** Non-identifying booking facts shared by every funnel event past selection. */
export function cruiseParams(selectedCruise, passengers) {
  return {
    cruise_type: selectedCruise?.cruiseName || '',
    deck: selectedCruise?.deckLabel || '',
    adults: passengers?.adults ?? 0,
    children: passengers?.children ?? 0,
    infants: passengers?.infants ?? 0,
  };
}

/** Public (B2C) value of the party for the selected cruise, in rupees. */
export function bookingValue(selectedCruise, passengers) {
  const priceAdult = selectedCruise?.priceAdult ?? 0;
  const priceChild = selectedCruise?.priceChild ?? 0;
  return (passengers?.adults ?? 0) * priceAdult + (passengers?.children ?? 0) * priceChild;
}

/** Ticketed seats — adults + children. Infants travel free and are not
 *  counted as purchased items. */
export function paidSeats(passengers) {
  return (passengers?.adults ?? 0) + (passengers?.children ?? 0);
}

// ── Traffic source (first touch, 30 days, consent-gated) ────────────────────
//
// Mirrors Layout.astro on the marketing site exactly. The landing URL's
// campaign parameters are remembered for this tab straight away (sessionStorage,
// never sent anywhere), and written to the shared ag_src cookie only once
// consent is granted — immediately if the visitor already accepted, otherwise
// on Accept. An existing ag_src is never overwritten (first touch wins), and a
// URL without campaign parameters never replaces one.

function landingParams() {
  const q = new URLSearchParams(window.location.search);
  const out = {};
  let any = false;
  for (const k of SOURCE_KEYS) {
    const v = (q.get(k) || '').trim().slice(0, 200);
    if (v) {
      out[k] = v;
      any = true;
    }
  }
  return any ? out : null;
}

function readPending() {
  try {
    return JSON.parse(window.sessionStorage.getItem(SOURCE_PENDING_KEY) || 'null');
  } catch (_) {
    return null;
  }
}

function clearPending() {
  try {
    window.sessionStorage.removeItem(SOURCE_PENDING_KEY);
  } catch (_) {
    /* storage unavailable — nothing to clear */
  }
}

function stashLanding() {
  const p = landingParams();
  if (!p || readPending()) return;
  p.ts = new Date().toISOString();
  try {
    window.sessionStorage.setItem(SOURCE_PENDING_KEY, JSON.stringify(p));
  } catch (_) {
    /* storage unavailable — the source is simply not remembered */
  }
}

function promoteLanding() {
  if (readConsent() !== 'granted') return;
  if (readCookie(SOURCE_COOKIE)) {
    clearPending();
    return;
  }
  const p = readPending();
  if (!p) return;
  const value = Object.keys(p)
    .map((k) => `${k}=${encodeURIComponent(p[k])}`)
    .join('&');
  document.cookie = `${SOURCE_COOKIE}=${value}; max-age=${SOURCE_MAX_AGE}; path=/${cookieDomainAttr()}; SameSite=Lax`;
  clearPending();
}

/** Run once when the app boots: remember the landing campaign, and write it
 *  to the cookie if consent already exists. Safe to call more than once. */
export function captureLanding() {
  if (typeof window === 'undefined') return;
  try {
    stashLanding();
    promoteLanding();
  } catch (_) {
    /* never let attribution break the app */
  }
}

/**
 * The traffic source to send with create-order, so the server can store it on
 * the payments row (the Razorpay webhook creates most bookings server-side,
 * where no cookie is readable, so the values must travel with the order).
 * ag_src holds the first-touch campaign; _fbp / _fbc are the pixel's own
 * cookies, kept for the Conversions API. Every value is null when absent —
 * the server treats the whole block as optional.
 */
export function readAttribution() {
  const out = {
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_content: null,
    fbclid: null,
    fbp: null,
    fbc: null,
  };
  try {
    const raw = readCookie(SOURCE_COOKIE);
    if (raw) {
      const q = new URLSearchParams(raw);
      for (const k of SOURCE_KEYS) {
        const v = (q.get(k) || '').trim();
        if (v) out[k] = v;
      }
    }
    out.fbp = readCookie('_fbp') || null;
    out.fbc = readCookie('_fbc') || null;
  } catch (_) {
    /* malformed cookie — send nothing rather than fail the order */
  }
  return out;
}

export { CURRENCY };
